// @ts-nocheck
import { MOCK, invalidateAndRefresh } from "@/state/app-store";
import { UI } from "@/components/ui";
import { Icon } from "@/components/icons";
import { api, gamesApi, packagesApi, adminApi } from "@/api/index";

const { $, $$, fmtNum, fmtMoney, fmtPercent, toast, confirmModal, hasPerm, statusBadge, trendArrow, statCard, channelChip, capabilityBadge, pageHeader, emptyState, primaryBtn, ghostBtn, stepIndicator, BUSINESS_COLORS, chartTooltip, chartXAxis, chartYAxis, chartLineSeries, chartBarSeries } = UI;

declare const echarts: any;
declare const Pages: Record<string, any>;
declare const PageHooks: { mounted?: ((path: string) => void) | null };
declare function navigate(path: string): void;
declare function getCurrentPath(): string;
declare function getCurrentPathWithQuery(): string;
declare function renderRoute(): void;

if (typeof window !== "undefined") {
  (window as any).Pages = (window as any).Pages || {};
}

const Pages = (window as any).Pages;

// ============================================================
// 游戏管理 - 游戏-包体 可折叠嵌套结构（商务风）
// ============================================================

window.Pages = window.Pages || {};

// 全局展开状态（页面级，刷新后重置）
window.__gameOpen = window.__gameOpen || {};

// ---------- 工具：派生包体级 App ID 与展示字段 ----------
// 注意：GameID / Game Secret 均为游戏级，同一游戏下所有包体共享同一套。
// 包体只生成/展示 Ripple ID、包体 App ID、商店链接等差异化信息。
function pkgCreds(pkg) {
  const region = pkg.region.toLowerCase();
  const platform = pkg.platform.toLowerCase();
  return {
    app_id: pkg.app_id || `app_${region}_${platform}_${pkg.id.slice(-3)}`,
    store_id: pkg.app_id || (pkg.platform === "iOS" ? `123456789${pkg.id.slice(-1)}` : pkg.bundle_id),
  };
}

const EASYSHARE_PACKAGE_PLATFORMS = [
  { code: "APP_STORE", name: "App Store", zhName: "苹果 App Store", appIdLabel: "Apple App ID" },
  { code: "GOOGLE_PLAY", name: "Google Play", zhName: "谷歌 Play", appIdLabel: "Google Play App ID" },
  { code: "ONESTORE", name: "ONE Store", zhName: "韩国 ONE Store", appIdLabel: "ONE Store App ID" },
  { code: "RUSTORE", name: "RuStore", zhName: "俄罗斯 RuStore", appIdLabel: "RuStore App ID" },
  { code: "APPGALLERY", name: "AppGallery", zhName: "华为应用市场", appIdLabel: "AppGallery App ID" },
  { code: "GALAXY_STORE", name: "Galaxy Store", zhName: "三星 Galaxy Store", appIdLabel: "Galaxy Store App ID" },
  { code: "AMAZON_APPSTORE", name: "Amazon Appstore", zhName: "亚马逊应用市场", appIdLabel: "Amazon Appstore App ID" },
  { code: "GETAPPS", name: "GetApps", zhName: "小米 GetApps", appIdLabel: "GetApps App ID" },
  { code: "OPPO_APP_MARKET", name: "OPPO App Market", zhName: "OPPO 软件商店", appIdLabel: "OPPO App ID" },
  { code: "VIVO_APP_STORE", name: "vivo App Store", zhName: "vivo 应用商店", appIdLabel: "vivo App ID" },
];
window.EASYSHARE_PACKAGE_PLATFORMS = EASYSHARE_PACKAGE_PLATFORMS;

function packagePlatformCode(platform) {
  const text = String(platform || "");
  const normalized = text.toUpperCase();
  const found = EASYSHARE_PACKAGE_PLATFORMS.find((item) => item.code === normalized || item.name.toUpperCase() === normalized || item.zhName === text);
  if (found) return found.code;
  if (/IOS|APP\s*STORE/i.test(text)) return "APP_STORE";
  if (/GOOGLE\s*PLAY|ANDROID/i.test(text)) return "GOOGLE_PLAY";
  if (/AMAZON/i.test(text) || /亚马逊/.test(text)) return "AMAZON_APPSTORE";
  return text || "APP_STORE";
}
window.packagePlatformCode = packagePlatformCode;

function packagePlatformMeta(platform) {
  const code = packagePlatformCode(platform);
  return EASYSHARE_PACKAGE_PLATFORMS.find((item) => item.code === code) || { code, name: code, zhName: code, appIdLabel: "平台 App ID" };
}
window.packagePlatformMeta = packagePlatformMeta;

function packagePlatformLabel(platform) {
  const meta = packagePlatformMeta(platform);
  return `${meta.zhName} / ${meta.name}`;
}
window.packagePlatformLabel = packagePlatformLabel;

function packageAppIdLabel(platform) {
  return packagePlatformMeta(platform).appIdLabel || "平台 App ID";
}

function packageDisplayName(pkg, game) {
  return pkg?.name || `${game?.code || game?.id || "game"}-${pkg?.region || "GLOBAL"}-${pkg?.platform || "平台"}`;
}

function packageRegionText(pkg) {
  const region = pkg.region || "GLOBAL";
  return `${region} · ${pkg.region_name || regionNameFromCode(region)}`;
}

const SHARE_REGION_COUNTRY_GROUPS = [
  { code: "NA", name: "North America", zhName: "北美", countries: [
    ["US", "United States", "美国"], ["CA", "Canada", "加拿大"], ["GL", "Greenland", "格陵兰"], ["BM", "Bermuda", "百慕大"], ["PM", "Saint Pierre and Miquelon", "圣皮埃尔和密克隆"],
  ]},
  { code: "LATAM", name: "Latin America", zhName: "拉丁美洲", countries: [
    ["MX", "Mexico", "墨西哥"], ["BZ", "Belize", "伯利兹"], ["GT", "Guatemala", "危地马拉"], ["HN", "Honduras", "洪都拉斯"], ["SV", "El Salvador", "萨尔瓦多"], ["NI", "Nicaragua", "尼加拉瓜"], ["CR", "Costa Rica", "哥斯达黎加"], ["PA", "Panama", "巴拿马"], ["CU", "Cuba", "古巴"], ["DO", "Dominican Republic", "多米尼加共和国"], ["HT", "Haiti", "海地"], ["JM", "Jamaica", "牙买加"], ["TT", "Trinidad and Tobago", "特立尼达和多巴哥"], ["BS", "Bahamas", "巴哈马"], ["BB", "Barbados", "巴巴多斯"], ["AG", "Antigua and Barbuda", "安提瓜和巴布达"], ["DM", "Dominica", "多米尼克"], ["GD", "Grenada", "格林纳达"], ["KN", "Saint Kitts and Nevis", "圣基茨和尼维斯"], ["LC", "Saint Lucia", "圣卢西亚"], ["VC", "Saint Vincent and the Grenadines", "圣文森特和格林纳丁斯"], ["AR", "Argentina", "阿根廷"], ["BO", "Bolivia", "玻利维亚"], ["BR", "Brazil", "巴西"], ["CL", "Chile", "智利"], ["CO", "Colombia", "哥伦比亚"], ["EC", "Ecuador", "厄瓜多尔"], ["GY", "Guyana", "圭亚那"], ["PY", "Paraguay", "巴拉圭"], ["PE", "Peru", "秘鲁"], ["SR", "Suriname", "苏里南"], ["UY", "Uruguay", "乌拉圭"], ["VE", "Venezuela", "委内瑞拉"],
  ]},
  { code: "EU", name: "Europe", zhName: "欧洲", countries: [
    ["AL", "Albania", "阿尔巴尼亚"], ["AD", "Andorra", "安道尔"], ["AT", "Austria", "奥地利"], ["BE", "Belgium", "比利时"], ["BA", "Bosnia and Herzegovina", "波黑"], ["BG", "Bulgaria", "保加利亚"], ["HR", "Croatia", "克罗地亚"], ["CY", "Cyprus", "塞浦路斯"], ["CZ", "Czechia", "捷克"], ["DK", "Denmark", "丹麦"], ["EE", "Estonia", "爱沙尼亚"], ["FI", "Finland", "芬兰"], ["FR", "France", "法国"], ["DE", "Germany", "德国"], ["GR", "Greece", "希腊"], ["HU", "Hungary", "匈牙利"], ["IS", "Iceland", "冰岛"], ["IE", "Ireland", "爱尔兰"], ["IT", "Italy", "意大利"], ["LV", "Latvia", "拉脱维亚"], ["LI", "Liechtenstein", "列支敦士登"], ["LT", "Lithuania", "立陶宛"], ["LU", "Luxembourg", "卢森堡"], ["MT", "Malta", "马耳他"], ["MD", "Moldova", "摩尔多瓦"], ["MC", "Monaco", "摩纳哥"], ["ME", "Montenegro", "黑山"], ["NL", "Netherlands", "荷兰"], ["MK", "North Macedonia", "北马其顿"], ["NO", "Norway", "挪威"], ["PL", "Poland", "波兰"], ["PT", "Portugal", "葡萄牙"], ["RO", "Romania", "罗马尼亚"], ["RS", "Serbia", "塞尔维亚"], ["SK", "Slovakia", "斯洛伐克"], ["SI", "Slovenia", "斯洛文尼亚"], ["ES", "Spain", "西班牙"], ["SE", "Sweden", "瑞典"], ["CH", "Switzerland", "瑞士"], ["GB", "United Kingdom", "英国"], ["SM", "San Marino", "圣马力诺"], ["VA", "Holy See", "梵蒂冈"],
  ]},
  { code: "CIS", name: "CIS / Russian-speaking", zhName: "独联体 / 俄语区", countries: [
    ["AM", "Armenia", "亚美尼亚"], ["AZ", "Azerbaijan", "阿塞拜疆"], ["BY", "Belarus", "白俄罗斯"], ["GE", "Georgia", "格鲁吉亚"], ["KZ", "Kazakhstan", "哈萨克斯坦"], ["KG", "Kyrgyzstan", "吉尔吉斯斯坦"], ["RU", "Russian Federation", "俄罗斯"], ["TJ", "Tajikistan", "塔吉克斯坦"], ["TM", "Turkmenistan", "土库曼斯坦"], ["UA", "Ukraine", "乌克兰"], ["UZ", "Uzbekistan", "乌兹别克斯坦"],
  ]},
  { code: "EA", name: "East Asia", zhName: "东亚", countries: [
    ["CN", "China", "中国"], ["HK", "Hong Kong", "中国香港"], ["MO", "Macao", "中国澳门"], ["TW", "Taiwan", "中国台湾"], ["JP", "Japan", "日本"], ["KR", "Korea, Republic of", "韩国"], ["KP", "Korea, Democratic People's Republic of", "朝鲜"], ["MN", "Mongolia", "蒙古"],
  ]},
  { code: "SEA", name: "Southeast Asia", zhName: "东南亚", countries: [
    ["BN", "Brunei Darussalam", "文莱"], ["KH", "Cambodia", "柬埔寨"], ["ID", "Indonesia", "印度尼西亚"], ["LA", "Lao People's Democratic Republic", "老挝"], ["MY", "Malaysia", "马来西亚"], ["MM", "Myanmar", "缅甸"], ["PH", "Philippines", "菲律宾"], ["SG", "Singapore", "新加坡"], ["TH", "Thailand", "泰国"], ["TL", "Timor-Leste", "东帝汶"], ["VN", "Viet Nam", "越南"],
  ]},
  { code: "SA", name: "South Asia", zhName: "南亚", countries: [
    ["AF", "Afghanistan", "阿富汗"], ["BD", "Bangladesh", "孟加拉国"], ["BT", "Bhutan", "不丹"], ["IN", "India", "印度"], ["MV", "Maldives", "马尔代夫"], ["NP", "Nepal", "尼泊尔"], ["PK", "Pakistan", "巴基斯坦"], ["LK", "Sri Lanka", "斯里兰卡"],
  ]},
  { code: "MENA", name: "Middle East & North Africa", zhName: "中东及北非", countries: [
    ["DZ", "Algeria", "阿尔及利亚"], ["BH", "Bahrain", "巴林"], ["EG", "Egypt", "埃及"], ["IR", "Iran", "伊朗"], ["IQ", "Iraq", "伊拉克"], ["IL", "Israel", "以色列"], ["JO", "Jordan", "约旦"], ["KW", "Kuwait", "科威特"], ["LB", "Lebanon", "黎巴嫩"], ["LY", "Libya", "利比亚"], ["MA", "Morocco", "摩洛哥"], ["OM", "Oman", "阿曼"], ["PS", "Palestine", "巴勒斯坦"], ["QA", "Qatar", "卡塔尔"], ["SA", "Saudi Arabia", "沙特阿拉伯"], ["SY", "Syrian Arab Republic", "叙利亚"], ["TN", "Tunisia", "突尼斯"], ["TR", "Türkiye", "土耳其"], ["AE", "United Arab Emirates", "阿联酋"], ["YE", "Yemen", "也门"],
  ]},
  { code: "SSA", name: "Sub-Saharan Africa", zhName: "撒哈拉以南非洲", countries: [
    ["AO", "Angola", "安哥拉"], ["BJ", "Benin", "贝宁"], ["BW", "Botswana", "博茨瓦纳"], ["BF", "Burkina Faso", "布基纳法索"], ["BI", "Burundi", "布隆迪"], ["CM", "Cameroon", "喀麦隆"], ["CV", "Cabo Verde", "佛得角"], ["CF", "Central African Republic", "中非共和国"], ["TD", "Chad", "乍得"], ["KM", "Comoros", "科摩罗"], ["CG", "Congo", "刚果共和国"], ["CD", "Congo, Democratic Republic of the", "刚果（金）"], ["CI", "Côte d'Ivoire", "科特迪瓦"], ["DJ", "Djibouti", "吉布提"], ["GQ", "Equatorial Guinea", "赤道几内亚"], ["ER", "Eritrea", "厄立特里亚"], ["SZ", "Eswatini", "斯威士兰"], ["ET", "Ethiopia", "埃塞俄比亚"], ["GA", "Gabon", "加蓬"], ["GM", "Gambia", "冈比亚"], ["GH", "Ghana", "加纳"], ["GN", "Guinea", "几内亚"], ["GW", "Guinea-Bissau", "几内亚比绍"], ["KE", "Kenya", "肯尼亚"], ["LS", "Lesotho", "莱索托"], ["LR", "Liberia", "利比里亚"], ["MG", "Madagascar", "马达加斯加"], ["MW", "Malawi", "马拉维"], ["ML", "Mali", "马里"], ["MR", "Mauritania", "毛里塔尼亚"], ["MU", "Mauritius", "毛里求斯"], ["MZ", "Mozambique", "莫桑比克"], ["NA", "Namibia", "纳米比亚"], ["NE", "Niger", "尼日尔"], ["NG", "Nigeria", "尼日利亚"], ["RW", "Rwanda", "卢旺达"], ["ST", "Sao Tome and Principe", "圣多美和普林西比"], ["SN", "Senegal", "塞内加尔"], ["SC", "Seychelles", "塞舌尔"], ["SL", "Sierra Leone", "塞拉利昂"], ["SO", "Somalia", "索马里"], ["ZA", "South Africa", "南非"], ["SS", "South Sudan", "南苏丹"], ["SD", "Sudan", "苏丹"], ["TZ", "Tanzania", "坦桑尼亚"], ["TG", "Togo", "多哥"], ["UG", "Uganda", "乌干达"], ["ZM", "Zambia", "赞比亚"], ["ZW", "Zimbabwe", "津巴布韦"],
  ]},
  { code: "OC", name: "Oceania", zhName: "大洋洲", countries: [
    ["AS", "American Samoa", "美属萨摩亚"], ["AU", "Australia", "澳大利亚"], ["CK", "Cook Islands", "库克群岛"], ["FJ", "Fiji", "斐济"], ["PF", "French Polynesia", "法属波利尼西亚"], ["GU", "Guam", "关岛"], ["KI", "Kiribati", "基里巴斯"], ["MH", "Marshall Islands", "马绍尔群岛"], ["FM", "Micronesia", "密克罗尼西亚"], ["NR", "Nauru", "瑙鲁"], ["NC", "New Caledonia", "新喀里多尼亚"], ["NZ", "New Zealand", "新西兰"], ["NU", "Niue", "纽埃"], ["MP", "Northern Mariana Islands", "北马里亚纳群岛"], ["PW", "Palau", "帕劳"], ["PG", "Papua New Guinea", "巴布亚新几内亚"], ["WS", "Samoa", "萨摩亚"], ["SB", "Solomon Islands", "所罗门群岛"], ["TO", "Tonga", "汤加"], ["TV", "Tuvalu", "图瓦卢"], ["VU", "Vanuatu", "瓦努阿图"], ["WF", "Wallis and Futuna", "瓦利斯和富图纳"],
  ]},
].map((group) => ({
  ...group,
  countries: group.countries.map(([code, name, zhName]) => ({ code, name, zhName, regionCode: group.code })),
}));

const SHARE_GLOBAL_REGION = { code: "GLOBAL", name: "Global", zhName: "全球", countries: [] };
const EASYSHARE_REGION_OPTIONS = [SHARE_GLOBAL_REGION, ...SHARE_REGION_COUNTRY_GROUPS].map((item) => ({
  code: item.code,
  name: item.name,
  zhName: item.zhName,
  value: item.code === "GLOBAL" ? "" : `${item.code} - ${item.name} / ${item.zhName}`,
}));
const EASYSHARE_REGION_GROUPS = [{ label: "Share 系统区域（Global + 10 大区域）", options: EASYSHARE_REGION_OPTIONS }];
const LEGACY_REGION_NAME_MAP = { APAC: "Asia Pacific / 亚太", ANZ: "Australia and New Zealand / 澳新", EMEA: "Europe, Middle East and Africa / 欧洲中东非洲", ME: "Middle East / 中东", GCC: "Gulf Cooperation Council / 海湾地区", "RU/CIS": "Russia and CIS / 俄罗斯及独联体" };
window.SHARE_REGION_COUNTRY_GROUPS = SHARE_REGION_COUNTRY_GROUPS;
window.SHARE_GLOBAL_REGION = SHARE_GLOBAL_REGION;
window.EASYSHARE_REGION_GROUPS = EASYSHARE_REGION_GROUPS;
window.EASYSHARE_REGION_OPTIONS = EASYSHARE_REGION_OPTIONS;

function regionOptionLabel(item) {
  return `${item.name} / ${item.zhName}`;
}
window.regionOptionLabel = regionOptionLabel;

function getShareRegionCountryGroups() {
  return SHARE_REGION_COUNTRY_GROUPS;
}
window.getShareRegionCountryGroups = getShareRegionCountryGroups;

function getShareRegionOptions() {
  return EASYSHARE_REGION_OPTIONS;
}
window.getShareRegionOptions = getShareRegionOptions;

function getShareCountryOptions() {
  return SHARE_REGION_COUNTRY_GROUPS.flatMap((group) => group.countries.map((country) => ({ ...country, regionName: group.name, regionZhName: group.zhName })));
}
window.getShareCountryOptions = getShareCountryOptions;

function regionNameFromCode(region) {
  const item = EASYSHARE_REGION_OPTIONS.find(r => r.code === region);
  if (item) return regionOptionLabel(item);
  const country = getShareCountryOptions().find((r) => r.code === region);
  if (country) return `${country.name} / ${country.zhName}`;
  return LEGACY_REGION_NAME_MAP[region] || region || "Global / 全球";
}
window.regionNameFromCode = regionNameFromCode;

function packageRegionSelectOptions(currentRegion) {
  const current = currentRegion || "GLOBAL";
  const opt = (item) => `<option value="${item.value}" ${item.code === current ? "selected" : ""}>${item.code} - ${regionOptionLabel(item)}</option>`;
  const standard = EASYSHARE_REGION_GROUPS.map(group => `
    <optgroup label="${group.label}">
      ${group.options.map(opt).join("")}
    </optgroup>`).join("");
  const hasCurrent = EASYSHARE_REGION_OPTIONS.some(item => item.code === current);
  const legacy = hasCurrent ? "" : `<optgroup label="历史区域代码"><option value="${current} - ${regionNameFromCode(current)}" selected>${current} - ${regionNameFromCode(current)}</option></optgroup>`;
  return standard + legacy;
}

function parsePackageRegion(regionRaw) {
  const region = regionRaw ? regionRaw.split(" - ")[0] : "GLOBAL";
  const regionName = regionNameFromCode(region);
  return { region, regionName };
}

// ---------- 切换游戏展开 ----------
window.toggleGameAccordion = function (gameId) {
  window.__gameOpen[gameId] = !window.__gameOpen[gameId];
  const wrap = document.getElementById(`game-row-${gameId}`);
  if (wrap) {
    wrap.outerHTML = renderGameAccordion(gameId);
    // 重新绑定可能需要的事件（暂无）
  }
};

// ---------- 复制到剪贴板 ----------
window.copyText = function (text, label) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => UI.toast(`${label || "已"}复制`, "success"));
  } else {
    const t = document.createElement("textarea");
    t.value = text;
    document.body.appendChild(t);
    t.select();
    document.execCommand("copy");
    t.remove();
    UI.toast(`${label || "已"}复制`, "success");
  }
};

// ============================================================
// 主页：游戏管理列表（嵌套结构）
// ============================================================
Pages.gameList = function () {
  const games = MOCK.GAMES;
  const totalPackages = MOCK.PACKAGES.length;
  const activeGames = games.filter(g => g.status === "active").length;
  // 最近创建：mock 简单用 g_003 的名字
  const latest = games.find(g => g.code === "match_hero") || games[games.length - 1];

  return `
    ${UI.pageHeader({ title: "游戏管理", desc: "管理所有接入 EasyShare 的游戏与包体" })}

    <!-- 顶部概览（4 项） -->
    <div class="grid grid-cols-4 gap-4 mb-4">
      ${overviewCard({ label: "负责游戏总数", value: games.length, sub: `${activeGames} 个活跃` })}
      ${overviewCard({ label: "包体总数", value: totalPackages, sub: `跨 ${games.length} 款游戏` })}
      ${overviewCard({ label: "最近创建", value: latest?.name || "—", sub: "2024-05-01", isText: true })}
      ${overviewQuickActions()}
    </div>


    <!-- 游戏可折叠卡片列表 -->
    <div class="space-y-2.5">
      ${games.map(g => renderGameAccordion(g.id)).join("")}
    </div>
  `;
};

// ============================================================
// 顶部概览卡（商务风：白底 + 极淡阴影 + 1px 边框）
// ============================================================
function overviewCard({ label, value, sub, isText }) {
  return `
    <div class="card p-4">
      <div class="text-xs text-ink-500 mb-1.5">${label}</div>
      <div class="${isText ? "text-lg font-semibold text-ink-900 truncate" : "text-3xl font-bold text-ink-900 num"}">${value}</div>
      <div class="text-xs text-ink-400 mt-1">${sub}</div>
    </div>
  `;
}

function overviewQuickActions() {
  return `
    <div class="card p-4">
      <div class="text-xs text-ink-500 mb-2">快捷操作</div>
      <div class="flex gap-2 mt-1.5">
        <button onclick="navigate('/dev/games/create')" class="flex-1 px-3 py-2 bg-brand-700 hover:bg-brand-800 text-white text-xs rounded-md font-medium flex items-center justify-center gap-1">
          ${Icon.plus({ size: 12, color: "#fff" })}
          <span>创建游戏</span>
        </button>
        <button onclick="navigate('/dev/packages/create')" class="flex-1 px-3 py-2 bg-ok-600 hover:bg-ok-700 text-white text-xs rounded-md font-medium flex items-center justify-center gap-1">
          ${Icon.plus({ size: 12, color: "#fff" })}
          <span>新增包体</span>
        </button>
      </div>
    </div>
  `;
}

// ============================================================
// 单个游戏的可折叠卡片
// ============================================================
function renderGameAccordion(gameId) {
  const game = MOCK.GAMES.find(g => g.id === gameId);
  if (!game) return "";
  const packages = MOCK.PACKAGES.filter(p => p.game_id === gameId);
  const open = !!window.__gameOpen[gameId];

  return `
    <div id="game-row-${gameId}" class="card overflow-hidden">
      <!-- 游戏头部（点击折叠） -->
      <div class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-ink-50/50" onclick="toggleGameAccordion('${gameId}')">
        <span class="text-ink-400 transition-transform ${open ? "rotate-90" : ""}">
          ${Icon.chevronRight({ size: 16, color: "#94A3B8", stroke: 2.4 })}
        </span>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2.5">
            <span class="text-base">${game.icon}</span>
            <span class="text-[15px] font-semibold text-ink-900">${game.name}</span>
            <span class="flex items-center gap-1 text-xs text-ink-500">
              ${Icon.gamepad({ size: 12, color: "#94A3B8" })}
              ${game.category}
            </span>
            ${gameStatusBadge(game.status)}
          </div>
          <div class="text-xs text-ink-500 mt-1 flex items-center gap-3">
            <span>包体: <strong class="text-ink-700">${packages.length}</strong> 个</span>
            <span class="text-ink-300">•</span>
            <span>创建于: 2024-03-10</span>
            <span class="text-ink-300">•</span>
            <span>GameID: <code class="font-mono text-[11px] text-ink-700">${game.id}</code></span>
            <span class="text-ink-300">•</span>
            <span>Game Secret: <span class="text-ink-400">游戏级共享</span></span>
          </div>
        </div>
        <div class="flex items-center gap-1.5 flex-shrink-0" onclick="event.stopPropagation()">
          <button onclick="navigate('/dev/games/${game.id}/packages/create')" class="px-2.5 py-1.5 bg-ok-50 hover:bg-ok-100 text-ok-700 text-xs rounded-md font-medium flex items-center gap-1">
            ${Icon.plus({ size: 12, color: "#15803D", stroke: 2.4 })}
            <span>新增包体</span>
          </button>
          <button onclick="navigate('/dev/games/${game.id}/edit')" class="p-1.5 hover:bg-ink-100 rounded-md text-ink-500" title="编辑游戏">
            ${Icon.edit({ size: 14, color: "#64748B", stroke: 2 })}
          </button>
        </div>
      </div>

      ${open ? renderPackagesPanel(game, packages) : ""}
    </div>
  `;
}

// ============================================================
// 展开后的包体列表面板
// ============================================================
function renderPackagesPanel(game, packages) {
  return `
    <div class="border-t border-ink-200 bg-ink-50/60 px-4 py-3">
      <div class="flex items-center justify-between mb-3">
        <span class="text-xs font-semibold text-ink-700">包体列表</span>
        <span class="text-xs text-ink-500"><strong>${packages.length}</strong> 个包体</span>
      </div>
      <div class="space-y-2">
        ${packages.map(p => renderPackageRow(p)).join("")}
        ${packages.length === 0 ? `
          <div class="text-center py-8 text-sm text-ink-400">
            该游戏暂无包体，<a onclick="navigate('/dev/games/${game.id}/packages/create')" class="text-brand-700 hover:underline cursor-pointer font-medium">立即创建</a>
          </div>` : ""}
      </div>
    </div>
  `;
}

// ============================================================
// 单个包体行（按新增包体字段定义展示）
// ============================================================
function renderPackageRow(pkg) {
  const game = MOCK.GAMES.find(g => g.id === pkg.game_id);
  const creds = pkgCreds(pkg);
  const platform = packagePlatformLabel(pkg.platform);
  const platformCode = packagePlatformCode(pkg.platform);
  const appIdLabel = packageAppIdLabel(pkg.platform);
  const platformIco = platformCode === "APP_STORE" ? Icon.apple({ size: 12, color: "#475569" }) : platformCode === "GOOGLE_PLAY" ? Icon.play({ size: 12, color: "#475569" }) : Icon.monitor({ size: 12, color: "#475569" });
  const flagEmoji = regionFlag(pkg.region);

  return `
    <div class="bg-white border border-ink-200 rounded-md p-3">
      <div class="flex items-start gap-2.5 mb-3">
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="text-sm font-semibold text-ink-900">${packageDisplayName(pkg, game)}</span>
            ${pkgStatusBadge(pkg.config_status)}
          </div>
          <div class="text-xs text-ink-500 mt-1 flex items-center gap-1.5 flex-wrap">
            <span>Ripple ID: <code class="font-mono text-[11px] text-ink-700">${pkg.id}</code></span>
            <span class="text-ink-300">•</span>
            <span class="flex items-center gap-1">${flagEmoji} ${packageRegionText(pkg)}</span>
            <span class="text-ink-300">•</span>
            <span class="flex items-center gap-1">${platformIco} ${platform}</span>
          </div>
        </div>
        <div class="flex items-center gap-0.5">
          <button onclick="navigate('/dev/packages')" class="p-1.5 hover:bg-ink-100 rounded-md text-ink-500" title="进入包体管理">
            ${Icon.settings({ size: 14, color: "#64748B", stroke: 2 })}
          </button>
          <button onclick="navigate('/ops/analytics/funnel')" class="p-1.5 hover:bg-ink-100 rounded-md text-ink-500" title="数据">
            ${Icon.chart({ size: 14, color: "#64748B", stroke: 2 })}
          </button>
          <button onclick="openEditPackageModal('${pkg.id}')" class="p-1.5 hover:bg-ink-100 rounded-md text-ink-500" title="编辑">
            ${Icon.edit({ size: 14, color: "#64748B", stroke: 2 })}
          </button>
          <button onclick="openDeletePackageModal('${pkg.id}')" class="p-1.5 hover:bg-danger-50 rounded-md text-ink-500 hover:text-danger-600" title="删除">
            ${Icon.trash({ size: 14, color: "#64748B", stroke: 2 })}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3">
        ${credField({ label: "Game ID（共享）", value: game?.id || pkg.game_id })}
        ${credField({ label: "Bundle ID / Package Name", value: pkg.bundle_id })}
        ${credField({ label: appIdLabel, value: creds.app_id })}
      </div>
      <div class="mt-2 text-[11px] text-ink-500 bg-ink-50 border border-ink-200 rounded-md px-2.5 py-1.5 break-all">
        应用商店链接：<code class="font-mono text-ink-700">${pkg.store_url || "—"}</code>；该包体继承游戏级 <strong>GameID</strong> 与 <strong>Game Secret</strong>。
      </div>
    </div>
  `;
}

// ============================================================
// App ID 字段（带复制按钮）
// ============================================================
function credField({ label, value }) {
  return `
    <div>
      <div class="flex items-center justify-between mb-1">
        <label class="text-[11px] text-ink-500">${label}</label>
        <button onclick="copyText('${value}', '${label}')" class="flex items-center gap-0.5 text-[11px] text-brand-600 hover:text-brand-800">
          ${Icon.copy({ size: 11, color: "#3B82F6", stroke: 2 })}
          <span>复制</span>
        </button>
      </div>
      <input type="text" value="${value}" readonly class="w-full px-2.5 py-1.5 bg-ink-50 border border-ink-200 rounded-md text-[12px] font-mono text-ink-900 focus:border-brand-400 outline-none" />
    </div>
  `;
}

// ============================================================
// 状态徽章（游戏 & 包体）
// ============================================================
function gameStatusBadge(status) {
  const map = {
    active: { txt: "稳定运营", bg: "bg-ok-50", color: "text-ok-700", dot: "bg-ok-500" },
    stable: { txt: "稳定运营", bg: "bg-ok-50", color: "text-ok-700", dot: "bg-ok-500" },
    launch: { txt: "发行初期", bg: "bg-brand-50", color: "text-brand-700", dot: "bg-brand-500" },
    draft: { txt: "筹备中", bg: "bg-ink-100", color: "text-ink-700", dot: "bg-ink-400" },
    paused: { txt: "暂停", bg: "bg-warn-50", color: "text-warn-700", dot: "bg-warn-500" },
    archived: { txt: "归档", bg: "bg-ink-100", color: "text-ink-500", dot: "bg-ink-300" },
  };
  const s = map[status] || map.draft;
  return `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${s.bg} ${s.color} text-[10px] font-medium">
    <span class="w-1 h-1 rounded-full ${s.dot}"></span>${s.txt}
  </span>`;
}

function pkgStatusBadge(status) {
  const map = {
    configured: { txt: "活跃", bg: "bg-ok-50", color: "text-ok-700", dot: "bg-ok-500" },
    inherited: { txt: "继承", bg: "bg-brand-50", color: "text-brand-700", dot: "bg-brand-500" },
    draft: { txt: "草稿", bg: "bg-ink-100", color: "text-ink-700", dot: "bg-ink-400" },
    paused: { txt: "停用", bg: "bg-warn-50", color: "text-warn-700", dot: "bg-warn-500" },
  };
  const s = map[status] || map.draft;
  return `<span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${s.bg} ${s.color} text-[10px] font-medium">
    <span class="w-1 h-1 rounded-full ${s.dot}"></span>${s.txt}
  </span>`;
}

function regionFlag(region) {
  const flags = {
    GLOBAL: "🌐", NA: "🌎", US: "🇺🇸", CA: "🇨🇦", MX: "🇲🇽", LATAM: "🌎", BR: "🇧🇷", AR: "🇦🇷", CL: "🇨🇱", CO: "🇨🇴",
    EMEA: "🌍", EU: "🇪🇺", MENA: "🌍", ME: "🌍", CIS: "🌍", "RU/CIS": "🌍", SSA: "🌍", GCC: "🌍", GB: "🇬🇧", DE: "🇩🇪", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹", NL: "🇳🇱", TR: "🇹🇷", KSA: "🇸🇦", AE: "🇦🇪",
    APAC: "🌏", EA: "🌏", SEA: "🌏", SA: "🌏", OC: "🌊", ANZ: "🇦🇺", JP: "🇯🇵", KR: "🇰🇷", TW: "🇹🇼", HK: "🇭🇰", SG: "🇸🇬", TH: "🇹🇭", ID: "🇮🇩", VN: "🇻🇳", PH: "🇵🇭", MY: "🇲🇾", IN: "🇮🇳", AU: "🇦🇺", NZ: "🇳🇿",
  };
  const countryCode = String(region || "").includes("-") ? String(region).split("-").pop() : region;
  return `<span class="text-[12px]">${flags[countryCode] || flags[region] || "🌐"}</span>`;
}

// ============================================================
// 创建游戏 - 中文名 + 英文名 + 类型 + 描述 + 图标 + 状态
// 创建成功后跳转到「凭证展示页」展示 GameID + Game Secret
// ============================================================

// 全局存放刚创建出来的游戏凭证（演示用，刷新即丢失）
window.__newGameCredential = window.__newGameCredential || null;

// 9 项预设图标候选
const PRESET_GAME_ICONS = ["👑", "🐉", "⚔️", "🍬", "🎮", "🏰", "🚀", "🎯", "🎲", "🏆", "💎", "🌟"];

// 游戏一级品类（创建游戏页使用）
const GAME_CATEGORIES = [
  { value: "casual", name: "Casual", zh: "休闲", icon: "🎲", desc: "轻量、低门槛、短局体验" },
  { value: "puzzle", name: "Puzzle", zh: "益智解谜", icon: "🧩", desc: "逻辑、物理、逃脱、脑力" },
  { value: "rpg", name: "RPG", zh: "角色扮演", icon: "⚔️", desc: "角色成长、剧情、养成" },
  { value: "strategy", name: "Strategy", zh: "策略", icon: "🏰", desc: "SLG、塔防、4X、RTS" },
  { value: "action", name: "Action", zh: "动作", icon: "🔥", desc: "操作反馈强、实时战斗" },
  { value: "shooter", name: "Shooter", zh: "射击", icon: "🎯", desc: "FPS、TPS、战术竞技" },
  { value: "simulation", name: "Simulation", zh: "模拟经营", icon: "🏙️", desc: "经营、建造、生活模拟" },
  { value: "sports_racing", name: "Sports & Racing", zh: "体育竞速", icon: "🏎️", desc: "体育、赛车、载具驾驶" },
  { value: "card_board", name: "Card & Board", zh: "卡牌桌游", icon: "🃏", desc: "卡牌、棋类、桌游" },
  { value: "casino", name: "Casino", zh: "博彩类", icon: "🎰", desc: "Slots、Poker、Bingo" },
  { value: "music", name: "Music & Rhythm", zh: "音乐节奏", icon: "🎵", desc: "音游、节奏、舞蹈" },
  { value: "social_party", name: "Social & Party", zh: "社交派对", icon: "🎉", desc: "派对、语音、UGC 社区" },
  { value: "educational", name: "Educational", zh: "教育", icon: "📚", desc: "儿童、语言、学习" },
  { value: "hybrid_casual", name: "Hybrid-casual", zh: "混合休闲", icon: "🧪", desc: "轻玩法 + 中度系统" },
  { value: "other", name: "Other", zh: "其他", icon: "🎮", desc: "暂未分类" },
];

// 创建游戏时必填的发行状态
const CREATE_GAME_STATUSES = [
  { value: "draft", label: "筹备中", desc: "研发/联调阶段，暂未发行" },
  { value: "launch", label: "发行初期", desc: "刚上线，重点观察数据" },
  { value: "stable", label: "稳定运营中", desc: "已进入长期运营阶段" },
];

Pages.gameCreate = function () {
  PageHooks.mounted = () => bindGameCreateForm();

  return `
    ${UI.pageHeader({ title: "创建新游戏", desc: "填写游戏基本信息，创建后将生成游戏唯一标识与密钥" })}

    <div class="grid grid-cols-3 gap-4">
      <!-- 左侧：表单 -->
      <div class="col-span-2">
        <form id="game-create-form" onsubmit="return submitGameCreate(event)">
          <!-- Section 1: 游戏图标（必选） -->
          <div class="card p-5 mb-4">
            <div class="flex items-center justify-between mb-3">
              <div>
                <h3 class="text-sm font-semibold text-ink-900">游戏图标 <span class="text-danger-600">*</span></h3>
                <p class="text-xs text-ink-500 mt-0.5">用于在控制台、SDK 调试器、分享卡片中识别游戏</p>
              </div>
              <span class="text-xs text-ink-400">已选：<span id="icon-preview-label" class="font-medium text-ink-700">未选择</span></span>
            </div>

            <div class="flex items-start gap-4">
              <!-- 当前预览 -->
              <div id="icon-preview-box" class="w-20 h-20 rounded-lg border border-dashed border-ink-300 bg-ink-50 flex items-center justify-center text-3xl flex-shrink-0">
                <span class="text-ink-300 text-xs">未选择</span>
              </div>
              <!-- 候选图标 -->
              <div class="flex-1">
                <div class="text-[11px] text-ink-500 mb-1.5">从预设中选择</div>
                <div class="grid grid-cols-12 gap-1.5">
                  ${PRESET_GAME_ICONS.map(em => `
                    <button type="button" onclick="selectGameIcon('${em}', this)" class="icon-pick aspect-square rounded-md border border-ink-200 bg-white hover:border-brand-400 hover:bg-brand-50 flex items-center justify-center text-lg">${em}</button>
                  `).join("")}
                </div>
                <div class="mt-2 flex items-center gap-2">
                  <button type="button" onclick="document.getElementById('game-icon-file').click()" class="px-2.5 py-1 text-xs border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700 flex items-center gap-1">
                    ${Icon.image({ size: 12, color: "#475569" })}
                    上传自定义图片
                  </button>
                  <span id="game-icon-file-name" class="text-[11px] text-ink-400">建议 512×512 px · PNG / JPG，&lt;500KB</span>
                </div>
                <input id="game-icon-file" type="file" accept="image/png,image/jpeg,image/webp" class="hidden" onchange="handleGameIconFile(this.files?.[0])" />
                <input id="game-icon-input" type="hidden" required />
              </div>
            </div>
          </div>

          <!-- Section 2: 基本信息 -->
          <div class="card p-5 mb-4">
            <h3 class="text-sm font-semibold text-ink-900 mb-3">基本信息</h3>

            <!-- 游戏名称（必选） -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-ink-700 mb-1.5">游戏中文名 <span class="text-danger-600">*</span></label>
                <input id="game-name-zh-input" type="text" required maxlength="50" class="w-full px-3 py-2 border border-ink-200 rounded-md focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none text-sm" placeholder="如：王者征途" oninput="updateNameCounter()" />
                <div class="flex items-center justify-between mt-1">
                  <p class="text-[11px] text-ink-400">面向中文后台、团队协作和内部识别展示</p>
                  <span class="text-[11px] text-ink-400"><span id="name-zh-counter">0</span>/50</span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-ink-700 mb-1.5">游戏英文名 <span class="text-danger-600">*</span></label>
                <input id="game-name-en-input" type="text" required maxlength="80" class="w-full px-3 py-2 border border-ink-200 rounded-md focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none text-sm" placeholder="如：King's Quest" oninput="updateNameCounter()" />
                <div class="flex items-center justify-between mt-1">
                  <p class="text-[11px] text-ink-400">面向海外商店、分享卡片和 SDK 识别展示</p>
                  <span class="text-[11px] text-ink-400"><span id="name-en-counter">0</span>/80</span>
                </div>
              </div>
            </div>

            <!-- 游戏类型（必选） -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-ink-700 mb-1.5">游戏类型 <span class="text-danger-600">*</span></label>
              <div class="grid grid-cols-3 gap-2">
                ${GAME_CATEGORIES.map(c => `
                  <label class="relative cursor-pointer">
                    <input type="radio" name="game-category" value="${c.value}" class="peer sr-only" onchange="document.getElementById('game-category-input').value='${c.value}|${c.name} / ${c.zh}'; validateGameCreate();" />
                    <div class="border border-ink-200 rounded-md p-2.5 hover:border-brand-400 peer-checked:border-brand-700 peer-checked:bg-brand-50 transition">
                      <div class="flex items-start gap-2">
                        <span class="text-base mt-0.5">${c.icon}</span>
                        <div class="flex-1 min-w-0">
                          <div class="text-xs font-semibold text-ink-900">${c.zh} <span class="font-normal text-ink-400">${c.name}</span></div>
                          <div class="text-[10px] text-ink-500 mt-0.5 truncate">${c.desc}</div>
                        </div>
                      </div>
                    </div>
                  </label>
                `).join("")}
              </div>
              <input id="game-category-input" type="hidden" required />
            </div>

            <!-- 游戏描述（可选） -->
            <div>
              <label class="block text-sm font-medium text-ink-700 mb-1.5">
                游戏描述
                <span class="ml-1 text-[10px] font-normal text-ink-400">（可选）</span>
              </label>
              <textarea id="game-desc-input" rows="3" maxlength="200" class="w-full px-3 py-2 border border-ink-200 rounded-md text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100" placeholder="一句话介绍游戏，便于团队识别（如：面向欧美东南亚市场的 MMORPG）" oninput="updateDescCounter()"></textarea>
              <div class="flex items-center justify-end mt-1">
                <span class="text-[11px] text-ink-400"><span id="desc-counter">0</span>/200</span>
              </div>
            </div>

            <!-- 发行状态（必选） -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-ink-700 mb-1.5">
                发行状态 <span class="text-danger-600">*</span>
              </label>
              <div class="grid grid-cols-3 gap-2">
                ${CREATE_GAME_STATUSES.map(s => `
                  <label class="relative cursor-pointer">
                    <input type="radio" name="game-status" value="${s.value}" class="peer sr-only" onchange="document.getElementById('game-status-input').value='${s.value}'; validateGameCreate();" />
                    <div class="border border-ink-200 rounded-md p-3 hover:border-brand-400 peer-checked:border-brand-700 peer-checked:bg-brand-50 transition">
                      <div class="text-xs font-semibold text-ink-900">${s.label}</div>
                      <div class="text-[10px] text-ink-500 mt-0.5">${s.desc}</div>
                    </div>
                  </label>
                `).join("")}
              </div>
              <input id="game-status-input" type="hidden" required />
            </div>
          </div>

          <!-- 提交按钮 -->
          <div class="flex justify-end gap-2">
            ${UI.ghostBtn("取消", { onclick: "navigate('/dev/games')" })}
            <button type="submit" id="game-create-submit" disabled class="px-5 py-2 rounded-md bg-brand-900 text-white text-sm font-medium hover:bg-brand-800 disabled:bg-ink-300 disabled:cursor-not-allowed flex items-center gap-1.5">
              ${Icon.plus({ size: 14, color: "#fff" })}
              <span>创建游戏</span>
            </button>
          </div>
        </form>
      </div>

      <!-- 右侧：创建后的资源说明 -->
      <div class="col-span-1">
        <div class="card p-5 sticky top-4">
          <div class="flex items-start gap-2.5 mb-3">
            <div class="w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center flex-shrink-0">
              ${Icon.info({ size: 16, color: "#1E3A8A" })}
            </div>
            <div>
              <h4 class="text-sm font-semibold text-ink-900">创建后将生成</h4>
              <p class="text-[11px] text-ink-500 mt-0.5">系统自动分配，无需手动填写</p>
            </div>
          </div>

          <div class="space-y-3">
            <div class="border-l-2 border-brand-700 pl-3 py-1">
              <div class="text-xs font-semibold text-ink-900">Game ID</div>
              <div class="text-[11px] text-ink-500 mt-0.5">游戏全局唯一标识，用于 SDK 调用、API 调度、数据归集</div>
              <div class="mt-1.5"><code class="text-[10px] font-mono px-1.5 py-0.5 bg-ink-100 rounded text-ink-600">game_xxxxxxxxxxxxxxxx</code></div>
            </div>

            <div class="border-l-2 border-warn-600 pl-3 py-1">
              <div class="text-xs font-semibold text-ink-900">Game Secret</div>
              <div class="text-[11px] text-ink-500 mt-0.5">同款游戏在不同区域、不同服务器与 SDK 后端通信时使用的根密钥</div>
              <div class="mt-1.5"><code class="text-[10px] font-mono px-1.5 py-0.5 bg-warn-50 rounded text-warn-700">gs_live_xxxxxxxxxxxx...</code></div>
            </div>
          </div>

          <div class="mt-4 pt-3 border-t border-ink-100">
            <div class="flex items-start gap-2 p-2.5 bg-warn-50 border border-warn-200 rounded-md">
              ${Icon.alert ? Icon.alert({ size: 14, color: "#B45309" }) : `<span class="text-warn-600 flex-shrink-0 mt-0.5">⚠️</span>`}
              <div class="text-[11px] text-warn-800">
                <strong>Game Secret 仅在创建后展示一次</strong>，请务必妥善保存。如丢失只能重置（重置后旧密钥立即失效）。
              </div>
            </div>
          </div>

          <div class="mt-3 text-[11px] text-ink-500 leading-relaxed">
            <div class="font-semibold text-ink-700 mb-1">创建后还需要：</div>
            <ol class="list-decimal pl-4 space-y-0.5">
              <li>添加包体（Region × Platform）</li>
              <li>接入 SDK（使用同一套 GameID / Game Secret）</li>
              <li>设置分享内容（标题 / 图片 / 渠道）</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  `;
};

// ---------- 表单交互逻辑 ----------
function bindGameCreateForm() {
  // 同步必填项校验，控制提交按钮可用性
  const inputs = ["game-name-zh-input", "game-name-en-input", "game-icon-input", "game-category-input", "game-status-input"];
  inputs.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.addEventListener("input", validateGameCreate);
    if (el) el.addEventListener("change", validateGameCreate);
  });
  validateGameCreate();
}

function validateGameCreate() {
  const nameZh = document.getElementById("game-name-zh-input")?.value.trim();
  const nameEn = document.getElementById("game-name-en-input")?.value.trim();
  const icon = document.getElementById("game-icon-input")?.value;
  const cat = document.getElementById("game-category-input")?.value;
  const status = document.getElementById("game-status-input")?.value;
  const btn = document.getElementById("game-create-submit");
  if (!btn || btn.dataset.busy === "1") return;
  const ready = !!(nameZh && nameEn && icon && cat && status);
  btn.disabled = !ready;
  btn.classList.toggle("opacity-60", !ready);
  btn.classList.toggle("opacity-100", ready);
}

window.selectGameIcon = function (emoji, btn) {
  document.getElementById("game-icon-input").value = emoji;
  document.getElementById("icon-preview-box").innerHTML = `<span class="text-4xl">${emoji}</span>`;
  document.getElementById("icon-preview-label").textContent = emoji;
  // 高亮选中
  document.querySelectorAll(".icon-pick").forEach(b => {
    b.classList.remove("border-brand-700", "bg-brand-100");
    b.classList.add("border-ink-200", "bg-white");
  });
  btn.classList.remove("border-ink-200", "bg-white");
  btn.classList.add("border-brand-700", "bg-brand-100");
  validateGameCreate();
};

window.updateNameCounter = function () {
  const zh = document.getElementById("game-name-zh-input")?.value || "";
  const en = document.getElementById("game-name-en-input")?.value || "";
  const zhCounter = document.getElementById("name-zh-counter");
  const enCounter = document.getElementById("name-en-counter");
  if (zhCounter) zhCounter.textContent = zh.length;
  if (enCounter) enCounter.textContent = en.length;
  validateGameCreate();
};

window.updateDescCounter = function () {
  const v = document.getElementById("game-desc-input")?.value || "";
  document.getElementById("desc-counter").textContent = v.length;
};

window.handleGameIconFile = function (file) {
  if (!file) return;
  if (!file.type.startsWith("image/")) {
    UI.toast("请选择图片文件", "error");
    return;
  }
  const maxSize = 500 * 1024;
  if (file.size > maxSize) {
    UI.toast("图片不能超过 500KB", "error");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const dataUrl = reader.result;
    document.getElementById("game-icon-input").value = dataUrl;
    document.getElementById("icon-preview-box").innerHTML = `<img src="${dataUrl}" alt="游戏图标" class="w-full h-full object-cover rounded-lg" />`;
    document.getElementById("icon-preview-label").textContent = file.name;
    document.getElementById("game-icon-file-name").textContent = `${file.name} · ${(file.size / 1024).toFixed(0)}KB`;
    document.querySelectorAll(".icon-pick").forEach(b => {
      b.classList.remove("border-brand-700", "bg-brand-100");
      b.classList.add("border-ink-200", "bg-white");
    });
    validateGameCreate();
  };
  reader.readAsDataURL(file);
};

window.submitGameCreate = async function (e) {
  e.preventDefault();
  const nameZh = document.getElementById("game-name-zh-input").value.trim();
  const nameEn = document.getElementById("game-name-en-input").value.trim();
  const icon = document.getElementById("game-icon-input").value;
  const catRaw = document.getElementById("game-category-input").value;
  const desc = document.getElementById("game-desc-input").value.trim();
  const status = document.getElementById("game-status-input")?.value;
  if (!nameZh || !nameEn || !icon || !catRaw || !status) {
    UI.toast("请补全必填字段", "error");
    return false;
  }
  const [, catName] = catRaw.split("|");

  // 防止重复提交
  if (window.__creating) return false;
  window.__creating = true;

  setFormDisabled(true);
  setSubmitLoading(true);

  try {
    const { data } = await gamesApi.create({
      name: nameZh,
      name_zh: nameZh,
      name_en: nameEn,
      code: nameEn.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 30) || undefined,
      category: catName,
      desc: desc || "—",
      status,
      icon,
      color: "from-slate-400 to-slate-600",
    });
    const newGame = data.game;
    const secret = data.secret;
    const gameId = newGame.id;
    const credential = { game: newGame, secret, createdAt: new Date() };
    window.__newGameCredential = credential;
    sessionStorage.setItem(`easyshare:newGameCredential:${gameId}`, JSON.stringify({
      game: newGame,
      secret,
      createdAt: credential.createdAt.toISOString(),
    }));
    await invalidateAndRefresh();
    UI.toast("游戏创建成功", "success");
    navigate(`/dev/games/${gameId}/credential`);
  } catch (err) {
    UI.toast(err?.message || "创建失败", "error");
    setFormDisabled(false);
    const btn = document.getElementById("game-create-submit");
    if (btn) {
      btn.dataset.busy = "0";
      btn.innerHTML = `<span>创建游戏</span>`;
      validateGameCreate();
    }
  } finally {
    window.__creating = false;
  }

  return false;
};

// ---------- 表单交互辅助 ----------
function setFormDisabled(disabled) {
  document.querySelectorAll("#game-create-form input, #game-create-form textarea, #game-create-form button[type='button']")
    .forEach(el => { el.disabled = disabled; });
  document.querySelectorAll("#game-create-form .icon-pick").forEach(el => {
    el.disabled = disabled;
    el.classList.toggle("pointer-events-none", disabled);
    el.classList.toggle("opacity-60", disabled);
  });
}

function setSubmitLoading(loading) {
  const btn = document.getElementById("game-create-submit");
  if (!btn) return;
  if (loading) {
    btn.disabled = true;
    btn.dataset.busy = "1";
    btn.innerHTML = `
      <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25"/>
        <path d="M12 2a10 10 0 0110 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
      </svg>
      <span>正在创建...</span>
    `;
  }
}

function generateGameId() {
  // game_ + 16 位 hex
  const hex = "0123456789abcdef";
  let s = "";
  for (let i = 0; i < 16; i++) s += hex[Math.floor(Math.random() * 16)];
  return "game_" + s;
}

function generateGameSecret() {
  // gs_live_ + 40 位 base62
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let s = "";
  for (let i = 0; i < 40; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return "gs_live_" + s;
}

// ---------- Ripple ID 生成（v2，包体级唯一标识） ----------
// 旧规则问题：仅取 Date.now() 末 6 位，同一 ~16.7 分钟窗口内必然重复循环，
// 且没有任何唯一性校验兜底，短时间内新建多个包体极易撞号，属于事故高发点。
//
// 新规则：
// 1. 前缀固定为 p_，与现有数据 / 文档 / UI 文案保持兼容；
// 2. 主体为 12 位 base36（0-9a-z）强随机字符，理论组合数 36^12 ≈ 4.7×10^18，
//    不再依赖时间戳，避免"同一时刻并发创建"场景下的碰撞风险；
// 3. 生成后与 MOCK.PACKAGES 现有 Ripple ID 做唯一性校验，一旦撞号立即重新生成，
//    作为最终兜底（正常情况下概率极低，但不依赖"极低概率"本身，做显式保证）。
function generateRippleId() {
  const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
  const randomSegment = () => {
    let s = "";
    for (let i = 0; i < 12; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return s;
  };
  let id;
  let guard = 0;
  do {
    id = `p_${randomSegment()}`;
    guard += 1;
  } while (MOCK.PACKAGES.some((pkg) => pkg.id === id) && guard < 20);
  return id;
}
window.generateRippleId = generateRippleId;

function formField(label, content, required = false) {
  return `
    <div>
      <label class="block text-sm font-semibold text-ink-700 mb-1.5">${label}${required ? '<span class="text-danger-600 ml-0.5">*</span>' : ""}</label>
      ${content}
    </div>
  `;
}

window.downloadCredential = function () {
  let cred = window.__newGameCredential;
  if (!cred) {
    const gameId = (location.hash.match(/\/dev\/games\/([^/]+)\/credential/) || [])[1];
    const raw = gameId ? sessionStorage.getItem(`easyshare:newGameCredential:${gameId}`) : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      cred = { ...parsed, createdAt: new Date(parsed.createdAt) };
      window.__newGameCredential = cred;
    }
  }
  if (!cred) return;
  const txt = `EasyShare 游戏凭证
============================
Game Name ZH: ${cred.game.name_zh || cred.game.name}
Game Name EN: ${cred.game.name_en || cred.game.code}
Game ID:      ${cred.game.id}
Game Secret: ${cred.secret}
Category:    ${cred.game.category}
Created At:  ${cred.createdAt.toISOString()}

⚠️ 警告：Game Secret 是高敏感信息，仅用于服务端 HMAC 签名。
请存储到密钥管理系统（KMS / Vault / 1Password），切勿提交到代码仓库或前端暴露。
丢失后只能重置，重置会让所有已部署服务端代码失效。
`;
  const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `easyshare-game-${cred.game.id}.txt`;
  a.click();
  URL.revokeObjectURL(url);
  UI.toast("凭证已下载，请妥善保存", "success");
};

window.ackAndContinue = function (gameId) {
  sessionStorage.setItem(`easyshare:gameCreatedDetailOverlay:${gameId}`, "1");
  // 离开后销毁内存和 sessionStorage 中的 secret
  window.__newGameCredential = null;
  sessionStorage.removeItem(`easyshare:newGameCredential:${gameId}`);
  navigate(`/dev/games/${gameId}`);
};

function gameDetailMetric({ label, value, sub, icon = "activity", tone = "brand" }) {
  const colorMap = {
    brand: { bg: "bg-brand-50", text: "text-brand-700", color: "#1E3A8A" },
    ok: { bg: "bg-ok-50", text: "text-ok-700", color: "#15803D" },
    warn: { bg: "bg-warn-50", text: "text-warn-700", color: "#B45309" },
    blue: { bg: "bg-blue-50", text: "text-blue-700", color: "#2563EB" },
  };
  const meta = colorMap[tone] || colorMap.brand;
  const iconHtml = Icon[icon] ? Icon[icon]({ size: 15, color: meta.color }) : "";
  return `
    <div class="card p-4 overflow-hidden relative">
      <div class="flex items-start justify-between gap-3">
        <div>
          <div class="text-xs text-ink-500">${label}</div>
          <div class="num text-2xl font-semibold text-ink-900 mt-1">${value}</div>
          <div class="text-xs text-ink-400 mt-1">${sub}</div>
        </div>
        <div class="w-9 h-9 rounded-lg ${meta.bg} ${meta.text} flex items-center justify-center">${iconHtml}</div>
      </div>
    </div>
  `;
}

function gameDetailInfoPill(label, value) {
  return `<div class="rounded-lg border border-white/15 bg-white/10 px-3 py-2"><div class="text-[10px] uppercase tracking-wide text-white/60">${label}</div><div class="text-sm font-semibold text-white mt-0.5 truncate">${value}</div></div>`;
}

// ============================================================
// 游戏详情（重新设计：总览头部 + 分区式 Tab）
// ============================================================
Pages.gameDetailLegacy = function (gameId) {
  const game = MOCK.GAMES.find(g => g.id === gameId);
  if (!game) {
    return UI.emptyState({ icon: "🔍", title: "游戏不存在", action: UI.primaryBtn("返回列表", { onclick: "navigate('/dev/games')" }) });
  }

  const tab = (location.hash.split("?tab=")[1] || "overview").split("&")[0];
  const packages = MOCK.PACKAGES.filter(p => p.game_id === game.id);
  const regions = Array.from(new Set(packages.map(p => p.region))).filter(Boolean);
  const platforms = Array.from(new Set(packages.map(p => packagePlatformCode(p.platform)))).filter(Boolean);
  const releasedConfigs = (MOCK.SHARE_CONFIGS || []).filter(cfg => (cfg.package_ids || []).some(id => packages.some(p => p.id === id)) && cfg.status === "released");

  PageHooks.mounted = () => {
    const el = document.getElementById("game-trend-chart");
    if (el && window.echarts) {
      const chart = echarts.getInstanceByDom(el) || echarts.init(el);
      chart.setOption({
        grid: { top: 42, right: 20, bottom: 34, left: 48 },
        tooltip: UI.chartTooltip(),
        legend: { top: 8, left: 12, itemWidth: 12, itemHeight: 8, textStyle: { color: "#64748B", fontSize: 11 } },
        xAxis: UI.chartXAxis(MOCK.TREND_DATA.dates),
        yAxis: UI.chartYAxis(),
        series: [
          UI.chartLineSeries("分享", MOCK.TREND_DATA.share, { color: "#1E3A8A", area: true }),
          UI.chartLineSeries("点击", MOCK.TREND_DATA.click, { color: "#2563EB" }),
          UI.chartLineSeries("安装", MOCK.TREND_DATA.install, { color: "#0F766E" }),
        ],
      });
      window.addEventListener("resize", () => chart.resize());
      setTimeout(() => chart.resize(), 0);
    }
  };

  const tabs = [
    { key: "overview", label: "总览" },
    { key: "info", label: "基本信息" },
    { key: "packages", label: `包体管理 (${packages.length})` },
    { key: "config", label: "游戏级分享配置" },
    { key: "analytics", label: "数据统计" },
  ];

  return `
    <div class="card overflow-hidden mb-5 bg-brand-900 text-white border-0">
      <div class="p-6 relative">
        <div class="absolute right-0 top-0 w-72 h-72 bg-white/5 rounded-full -translate-y-24 translate-x-16"></div>
        <div class="relative z-10 flex items-start justify-between gap-6">
          <div class="min-w-0">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-2xl">${game.icon}</div>
              <div>
                <div class="flex items-center gap-2 flex-wrap">
                  <h1 class="text-xl font-semibold">${game.name}</h1>
                  ${gameStatusBadge(game.status).replace("bg-ink-100 text-ink-700", "bg-white/15 text-white")}
                </div>
                <div class="text-sm text-white/70 mt-1">${game.category} · ${game.desc}</div>
              </div>
            </div>
            <div class="grid grid-cols-4 gap-3 mt-5 max-w-4xl">
              ${gameDetailInfoPill("GameID", `<code class=\"font-mono\">${game.id}</code>`)}
              ${gameDetailInfoPill("Game Code", `<code class=\"font-mono\">${game.code}</code>`)}
              ${gameDetailInfoPill("覆盖区域", regions.length ? `${regions.length} 个` : "—")}
              ${gameDetailInfoPill("平台", platforms.length ? `${platforms.length} 类` : "—")}
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button onclick="navigate('/dev/games/${game.id}/edit')" class="px-3 py-1.5 text-sm bg-white/10 border border-white/15 hover:bg-white/15 rounded-lg flex items-center gap-1.5 text-white">
              ${Icon.edit({ size: 13, color: "#fff" })} 编辑游戏
            </button>
            ${UI.hasPerm("dev_tools") ? `<button onclick="navigate('/dev/games/${game.id}/packages/create')" class="px-3 py-1.5 text-sm bg-white text-brand-900 hover:bg-brand-50 rounded-lg font-medium flex items-center gap-1.5">
              ${Icon.plus({ size: 13, color: "#1E3A8A" })} 添加包体
            </button>` : ""}
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-4 gap-4 mb-5">
      ${gameDetailMetric({ label: "包体数量", value: packages.length, sub: `${regions.length || 0} 个区域 · ${platforms.length || 0} 类平台`, icon: "package", tone: "brand" })}
      ${gameDetailMetric({ label: "今日分享", value: UI.fmtNum(game.today.share), sub: "+12% vs 昨日", icon: "send", tone: "blue" })}
      ${gameDetailMetric({ label: "总转化率", value: `${game.convert}%`, sub: "近 7 天分享链路", icon: "trendingUp", tone: "ok" })}
      ${gameDetailMetric({ label: "已发布配置", value: releasedConfigs.length, sub: "活动 / 卡片 / 落地页", icon: "check", tone: "warn" })}
    </div>

    <div class="card overflow-hidden">
      <div class="px-4 pt-3 border-b border-ink-100 bg-white">
        <div class="flex items-center gap-1 overflow-x-auto">
          ${tabs.map(t => `<a href="#/dev/games/${game.id}?tab=${t.key}" class="px-3 py-2 text-sm border-b-2 whitespace-nowrap ${tab === t.key ? "border-brand-900 text-brand-900 font-semibold" : "border-transparent text-ink-500 hover:text-ink-900"}">${t.label}</a>`).join("")}
        </div>
      </div>
      <div class="p-5 bg-ink-50/40">${renderGameTab(tab, game)}</div>
    </div>
  `;
};

// 研发工具下的游戏详情页使用与编辑游戏一致的信息架构：顶部摘要 + 左侧目录 + 右侧详情表单。
Pages.gameDetail = function (gameId) {
  if (typeof Pages.gameEdit !== "function") {
    return UI.emptyState({ icon: "🔍", title: "游戏详情暂不可用", action: UI.primaryBtn("返回列表", { onclick: "navigate('/dev/games')" }) });
  }
  return Pages.gameEdit(gameId)
    .replace(new RegExp(`#/dev/games/${gameId}/edit\\?tab=`, "g"), `#/dev/games/${gameId}?tab=`)
    .replace(">编辑游戏</div>", ">游戏详情</div>");
};

function renderGameTab(tab, game) {
  const packages = MOCK.PACKAGES.filter(p => p.game_id === game.id);
  const releasedConfigs = (MOCK.SHARE_CONFIGS || []).filter(cfg => (cfg.package_ids || []).some(id => packages.some(p => p.id === id)) && cfg.status === "released");
  const regions = Array.from(new Set(packages.map(p => p.region))).filter(Boolean);
  const platformLabels = Array.from(new Set(packages.map(p => packagePlatformLabel(p.platform)))).filter(Boolean);

  if (tab === "overview") {
    return `
      <div class="grid grid-cols-12 gap-5 items-start">
        <section class="col-span-8 space-y-5">
          <div class="card p-5">
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="text-sm font-semibold text-ink-900">接入概览</h3>
                <p class="text-xs text-ink-500 mt-1">展示当前游戏的包体覆盖、配置接入和 SDK 初始化关键信息。</p>
              </div>
              <span class="badge bg-brand-50 text-brand-700">GameID 游戏级唯一</span>
            </div>
            <div class="grid grid-cols-3 gap-3">
              ${infoCell("覆盖区域", regions.length ? regions.join(" / ") : "—")}
              ${infoCell("平台类型", platformLabels.length ? platformLabels.join(" / ") : "—")}
              ${infoCell("已发布配置", `${releasedConfigs.length} 个`)}
              ${infoCell("Game Secret", `<span class="text-ink-500">游戏级共享，不按包体生成</span>`, true)}
            </div>
          </div>
          <div class="card p-5">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-sm font-semibold text-ink-900">最近包体</h3>
              <button onclick="navigate('/dev/games/${game.id}?tab=packages')" class="text-xs text-brand-700 hover:underline">查看全部</button>
            </div>
            <div class="space-y-2">${packages.slice(0, 3).map(p => renderPackageRow(p)).join("") || `<div class="text-sm text-ink-400 py-8 text-center">暂无包体</div>`}</div>
          </div>
        </section>
        <aside class="col-span-4 space-y-4">
          <div class="card p-5">
            <h3 class="text-sm font-semibold text-ink-900 mb-3">接入状态</h3>
            ${[
              ["游戏基础信息", "已完成", "bg-ok-50 text-ok-700"],
              ["包体 App ID", packages.length ? "已配置" : "待配置", packages.length ? "bg-ok-50 text-ok-700" : "bg-warn-50 text-warn-700"],
              ["分享配置", releasedConfigs.length ? "已发布" : "待发布", releasedConfigs.length ? "bg-ok-50 text-ok-700" : "bg-warn-50 text-warn-700"],
              ["SDK 初始化", "使用游戏级 GameID", "bg-brand-50 text-brand-700"],
            ].map(([label, value, tone]) => `<div class="flex items-center justify-between py-2 border-b border-ink-100 last:border-0"><span class="text-sm text-ink-600">${label}</span><span class="badge ${tone}">${value}</span></div>`).join("")}
          </div>
          <div class="card p-5 bg-ink-900 text-white border-0">
            <div class="text-xs text-white/60">下一步建议</div>
            <div class="text-sm font-semibold mt-1">完善包体维度信息</div>
            <p class="text-xs text-white/70 leading-5 mt-2">确保每个 Region × Platform 包体都有 Package ID / App ID / Store URL，运营侧才能准确做分享配置和实验筛选。</p>
            <button onclick="navigate('/dev/games/${game.id}/packages/create')" class="mt-4 px-3 py-1.5 rounded-lg bg-white text-ink-900 text-xs font-medium">添加包体</button>
          </div>
        </aside>
      </div>
    `;
  }

  if (tab === "info") {
    return `
      <div class="card p-5">
        <div class="grid grid-cols-2 gap-x-12 gap-y-5 max-w-4xl">
          ${infoCell("游戏代码（唯一标识）", `<div class="flex items-center gap-2"><code class="font-mono text-sm">${game.code}</code><span class="text-xs text-ink-400">/</span><code class="font-mono text-sm text-brand-700">${game.id}</code></div>`, true)}
          ${infoCell("游戏中文名", game.name_zh || game.name)}
          ${infoCell("游戏英文名", game.name_en || game.code)}
          ${infoCell("游戏类型", game.category)}
          ${infoCell("游戏描述", game.desc, true)}
          ${infoCell("状态", gameStatusBadge(game.status))}
          ${infoCell("创建时间", "2024-01-15 10:23")}
        </div>
      </div>
    `;
  }
  if (tab === "packages") {
    return `
      <div class="card overflow-hidden">
        <div class="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
          <div>
            <h3 class="text-sm font-semibold text-ink-900">包体管理</h3>
            <p class="text-xs text-ink-500 mt-0.5">研发侧维护 Region × Platform 包体和平台 App 信息。</p>
          </div>
          ${UI.hasPerm("dev_tools") ? UI.primaryBtn("+ 添加包体", { onclick: `navigate('/dev/games/${game.id}/packages/create')` }) : ""}
        </div>
        <div class="p-4 space-y-2">
          ${packages.map(p => renderPackageRow(p)).join("") || `<div class="text-sm text-ink-400 py-8 text-center">暂无包体</div>`}
        </div>
      </div>
    `;
  }
  if (tab === "config") {
    return `
      <div class="card p-5">
        <div class="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-5 text-sm flex gap-3">
          <div class="w-8 h-8 rounded-lg bg-brand-700 text-white flex items-center justify-center flex-shrink-0">${Icon.info({ size: 14, color: "#fff" })}</div>
          <div class="text-brand-900 leading-6"><strong>游戏级配置</strong>是该游戏下所有包体的默认配置。包体可以继承游戏级配置，也可以在包体级别覆盖多语言、渠道参数等字段。</div>
        </div>
        <div class="grid grid-cols-2 gap-6">
          <div class="space-y-4">
            ${infoCell("默认分享标题", `Join me in ${game.name_en || game.name}!`)}
            ${infoCell("默认分享描述", "Download now and let's play together!")}
            ${infoCell("默认归因窗口", `<span class="num font-semibold">7 天</span>`)}
            ${infoCell("追踪开关", gameStatusBadge("active"))}
          </div>
          <div>
            <div class="text-xs text-ink-500 mb-2">默认启用渠道</div>
            <div class="flex flex-wrap gap-1.5">${MOCK.CHANNELS.filter(c => c.enabled).map(c => UI.channelChip(c)).join("")}</div>
          </div>
        </div>
        <div class="mt-6 pt-5 border-t border-ink-100 flex justify-end">${UI.primaryBtn("编辑游戏级配置")}</div>
      </div>
    `;
  }
  if (tab === "analytics") {
    return `
      <div class="grid grid-cols-4 gap-3 mb-5">
        ${gameDetailMetric({ label: "近 7 天分享", value: UI.fmtNum(58234), sub: "+12% vs 上周", icon: "send", tone: "brand" })}
        ${gameDetailMetric({ label: "近 7 天点击", value: UI.fmtNum(17823), sub: "+8% vs 上周", icon: "chart", tone: "blue" })}
        ${gameDetailMetric({ label: "近 7 天安装", value: UI.fmtNum(4892), sub: "-2% vs 上周", icon: "package", tone: "ok" })}
        ${gameDetailMetric({ label: "近 7 天收入", value: UI.fmtMoney(28450), sub: "+18% vs 上周", icon: "trendingUp", tone: "warn" })}
      </div>
      <div class="card p-5">
        <div class="flex items-center justify-between mb-3"><div><div class="text-sm font-semibold text-ink-900">14 天分享趋势</div><div class="text-xs text-ink-500 mt-0.5">分享、点击、安装三条链路趋势</div></div><a href="#/ops/analytics/funnel" class="text-sm text-brand-700 hover:underline font-medium">查看完整漏斗分析 →</a></div>
        <div id="game-trend-chart" style="height: 320px;"></div>
      </div>
    `;
  }
  return "";
}

function infoCell(label, value, span2 = false) {
  return `
    <div ${span2 ? 'class="col-span-2"' : ""}>
      <div class="text-xs text-ink-500">${label}</div>
      <div class="text-sm mt-1 text-ink-900">${value}</div>
    </div>
  `;
}

// ============================================================
// 包体列表（跨游戏，按新增包体字段定义展示）
// ============================================================
window.__packageFilters = window.__packageFilters || { gameId: "", region: "", platform: "" };

window.setPackageFilter = function (key, value) {
  window.__packageFilters = { ...(window.__packageFilters || {}), [key]: value || "" };
  renderRoute();
};

window.resetPackageFilters = function () {
  window.__packageFilters = { gameId: "", region: "", platform: "" };
  renderRoute();
};

function packageFilterBtn(kind, value, label, activeValue) {
  const active = (activeValue || "") === (value || "");
  return `<button onclick="setPackageFilter('${kind}', '${value}')" class="px-2.5 py-1 text-xs rounded-md border ${active ? "bg-brand-50 border-brand-300 text-brand-700 font-medium" : "border-ink-200 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700"}">${label}</button>`;
}

Pages.packageList = function () {
  const filters = window.__packageFilters || { gameId: "", region: "", platform: "" };
  const allPackages = MOCK.PACKAGES.map(p => {
    const game = MOCK.GAMES.find(g => g.id === p.game_id);
    const creds = pkgCreds(p);
    return {
      ...p,
      game,
      gameName: game?.name,
      gameCode: game?.code,
      gameIcon: game?.icon,
      displayName: packageDisplayName(p, game),
      platformLabel: packagePlatformLabel(p.platform),
      appIdLabel: packageAppIdLabel(p.platform),
      appId: creds.app_id,
    };
  });
  const regionOptions = EASYSHARE_REGION_OPTIONS.map(r => r.code);
  const platformOptions = EASYSHARE_PACKAGE_PLATFORMS;
  const filteredPackages = allPackages.filter(p => {
    const matchGame = !filters.gameId || p.game_id === filters.gameId;
    const matchRegion = !filters.region || p.region === filters.region;
    const matchPlatform = !filters.platform || packagePlatformCode(p.platform) === filters.platform;
    return matchGame && matchRegion && matchPlatform;
  });
  const activeGame = filters.gameId ? MOCK.GAMES.find(g => g.id === filters.gameId) : null;

  return `
    ${UI.pageHeader({ title: "包体管理", desc: "按 Ripple ID、游戏代码、Region、Platform 与商店信息统一管理所有包体", actions: `<button onclick="document.getElementById('package-csv-file').click()" class="px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700">批量导入 CSV</button><input id="package-csv-file" type="file" accept=".csv,text/csv" class="hidden" onchange="handlePackageCsvFile(this.files?.[0])" />` })}

    <div class="card p-3 mb-4 flex items-center gap-3 flex-wrap">
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-ink-500">游戏</span>
        <select onchange="setPackageFilter('gameId', this.value)" class="text-sm border border-ink-200 rounded-md px-2.5 py-1.5 bg-white">
          <option value="" ${!filters.gameId ? "selected" : ""}>全部游戏</option>
          ${MOCK.GAMES.map(g => `<option value="${g.id}" ${filters.gameId === g.id ? "selected" : ""}>${g.code} / ${g.name}</option>`).join("")}
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-ink-500">国家/区域</span>
        <button onclick="navigate('/admin/platform/regions-countries?return=' + encodeURIComponent(getCurrentPathWithQuery()))" class="text-xs text-brand-700 hover:underline">统一配置</button>
        <select onchange="setPackageFilter('region', this.value)" class="text-sm border border-ink-200 rounded-md px-2.5 py-1.5 bg-white">
          <option value="" ${!filters.region ? "selected" : ""}>全部国家/区域</option>
          ${regionOptions.map(r => `<option value="${r}" ${filters.region === r ? "selected" : ""}>${r} - ${regionNameFromCode(r)}</option>`).join("")}
        </select>
      </div>
      <div class="flex items-center gap-1.5">
        <span class="text-xs text-ink-500">平台</span>
        <div class="flex gap-1">
          ${packageFilterBtn("platform", "", "全部", filters.platform)}
          ${platformOptions.map(p => packageFilterBtn("platform", p.code, `${p.zhName} / ${p.name}`, filters.platform)).join("")}
        </div>
      </div>
      <div class="flex-1"></div>
      <button onclick="resetPackageFilters()" class="px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700">重置筛选</button>
      <button onclick="navigate('/dev/packages/create')" class="px-3 py-1.5 text-sm bg-brand-900 hover:bg-brand-800 text-white rounded-md">创建包体</button>
    </div>

    <div class="mb-3 flex items-center justify-between text-xs text-ink-500">
      <div>
        当前显示 <strong class="text-ink-900 num">${filteredPackages.length}</strong> / ${allPackages.length} 个包体
        ${activeGame ? `<span class="ml-2 badge bg-brand-50 text-brand-700">${activeGame.code}</span>` : ""}
        ${filters.region ? `<span class="ml-1 badge bg-ink-100 text-ink-700">${filters.region}</span>` : ""}
        ${filters.platform ? `<span class="ml-1 badge bg-ink-100 text-ink-700">${packagePlatformLabel(filters.platform)}</span>` : ""}
      </div>
      <div>筛选会立即生效，可组合使用游戏、地区和平台条件。</div>
    </div>

    <div class="card overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-ink-50 text-xs text-ink-500">
          <tr>
            <th class="text-left px-5 py-3 font-semibold">游戏</th>
            <th class="text-left px-4 py-3 font-semibold">Ripple ID / 包体名称</th>
            <th class="text-left px-4 py-3 font-semibold">Region / Platform</th>
            <th class="text-left px-4 py-3 font-semibold">Bundle ID / App ID</th>
            <th class="text-left px-4 py-3 font-semibold">应用商店链接</th>
            <th class="text-left px-4 py-3 font-semibold">凭证继承</th>
            <th class="text-left px-4 py-3 font-semibold">配置</th>
            <th class="text-right px-5 py-3 font-semibold">操作</th>
          </tr>
        </thead>
        <tbody>
          ${filteredPackages.length ? filteredPackages.map(p => `
            <tr class="border-t border-ink-100 row-hover align-top">
              <td class="px-5 py-3">
                <div class="flex items-start gap-2">
                  <span class="text-base">${p.gameIcon || "🎮"}</span>
                  <div>
                    <div class="font-semibold text-ink-900">${p.gameName || "未知游戏"}</div>
                    <code class="text-[11px] text-ink-500 font-mono">${p.gameCode || p.game_id}</code>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="font-semibold text-ink-900">${p.displayName}</div>
                <div class="mt-1 flex items-center gap-1.5">
                  <code class="font-mono text-[11px] px-1.5 py-0.5 bg-brand-50 text-brand-700 rounded">${p.id}</code>
                  <button onclick="copyText('${p.id}', 'Ripple ID ')" class="text-[11px] text-brand-700 hover:underline">复制</button>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="text-xs text-ink-700">${packageRegionText(p)}</div>
                <div class="mt-1"><span class="badge bg-ink-100 text-ink-700">${p.platformLabel}</span></div>
              </td>
              <td class="px-4 py-3">
                <div class="font-mono text-xs text-ink-700 break-all">${p.bundle_id}</div>
                <div class="text-[11px] text-ink-500 mt-1">${p.appIdLabel}: <code class="font-mono text-ink-700">${p.appId}</code></div>
              </td>
              <td class="px-4 py-3 max-w-[220px]">
                <div class="font-mono text-[11px] text-ink-600 break-all">${p.store_url || "—"}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-[11px] text-ink-500">Game ID</div>
                <code class="font-mono text-xs text-brand-700">${p.game?.id || p.game_id}</code>
                <div class="text-[11px] text-warn-700 mt-1">Game Secret 共享</div>
              </td>
              <td class="px-4 py-3">${pkgStatusBadge(p.config_status)}</td>
              <td class="px-5 py-3 text-right whitespace-nowrap">
                <button onclick="navigate('/ops/share-config/${p.id}/expert')" class="text-xs text-brand-700 hover:underline font-medium mr-2">配置</button>
                <button onclick="openEditPackageModal('${p.id}')" class="text-xs text-ink-600 hover:underline font-medium mr-2">编辑</button>
                <button onclick="openDeletePackageModal('${p.id}')" class="text-xs text-danger-600 hover:underline font-medium">删除</button>
              </td>
            </tr>`).join("") : `
            <tr class="border-t border-ink-100">
              <td colspan="8" class="px-6 py-12 text-center">
                <div class="text-sm font-medium text-ink-700">没有匹配的包体</div>
                <div class="text-xs text-ink-500 mt-1">请调整游戏、地区或平台筛选条件。</div>
                <button onclick="resetPackageFilters()" class="mt-3 px-3 py-1.5 text-sm border border-ink-200 rounded-md hover:bg-ink-50 text-ink-700">重置筛选</button>
              </td>
            </tr>`}
        </tbody>
      </table>
    </div>
  `;
};

// ---------- 包体 CSV 导入：调用系统文件选择器并显示基础反馈 ----------
window.handlePackageCsvFile = function (file) {
  if (!file) return;
  const isCsv = file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
  if (!isCsv) {
    UI.toast("请选择 CSV 文件", "error");
    return;
  }
  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    UI.toast("CSV 文件不能超过 2MB", "error");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const text = String(reader.result || "");
    const rows = text.split(/\r?\n/).filter(Boolean).length;
    UI.toast(`已读取 ${file.name}，约 ${Math.max(rows - 1, 0)} 条包体记录`, "success");
  };
  reader.readAsText(file);
};

// ---------- 包体操作流水记录 ----------
async function addPackageAuditLog({ user, action, target, detail }) {
  try {
    await adminApi.createAuditLog({
      time: new Date().toLocaleString("zh-CN", { hour12: false }),
      user: user || MOCK.ROLES[MOCK.SESSION.currentRole]?.user || "当前用户",
      action,
      target,
      detail,
    });
    await invalidateAndRefresh();
  } catch (_) {
    // best-effort audit; mutations already succeeded
  }
}

// ---------- 编辑包体 ----------
window.openEditPackageModal = function (packageId) {
  const pkg = MOCK.PACKAGES.find(p => p.id === packageId);
  if (!pkg) return;
  const game = MOCK.GAMES.find(g => g.id === pkg.game_id);
  const creds = pkgCreds(pkg);
  const normalizedPlatform = packagePlatformCode(pkg.platform);
  const appIdLabel = packageAppIdLabel(pkg.platform);
  const displayName = packageDisplayName(pkg, game);
  const html = `
    <div class="fixed inset-0 z-50 flex items-center justify-center" id="edit-pkg-mask">
      <div class="absolute inset-0 bg-slate-900/35 backdrop-blur-[1px]" onclick="document.getElementById('edit-pkg-mask').remove()"></div>
      <div class="relative bg-white rounded-xl shadow-pop w-[680px] p-6 border border-ink-200 animate-fade-up">
        <div class="flex items-start justify-between mb-5">
          <div>
            <h3 class="text-lg font-semibold text-ink-900">编辑包体</h3>
            <p class="text-xs text-ink-500 mt-1">字段与创建包体保持一致；Ripple ID 和游戏级凭证不可编辑。</p>
          </div>
          <button onclick="document.getElementById('edit-pkg-mask').remove()" class="w-8 h-8 rounded-lg hover:bg-ink-50 text-ink-400">✕</button>
        </div>

        <div class="bg-ink-50 border border-ink-200 rounded-lg p-3 mb-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div class="text-ink-500 mb-1">Ripple ID</div>
            <code class="font-mono text-brand-700">${pkg.id}</code>
          </div>
          <div>
            <div class="text-ink-500 mb-1">包体名称</div>
            <div class="font-semibold text-ink-900 truncate">${displayName}</div>
          </div>
          <div>
            <div class="text-ink-500 mb-1">所属游戏</div>
            <span class="font-medium text-ink-900">${game?.name || "未知游戏"}</span>
          </div>
          <div>
            <div class="text-ink-500 mb-1">Game ID（共享）</div>
            <code class="font-mono text-brand-700">${game?.id || pkg.game_id}</code>
          </div>
        </div>

        <form onsubmit="event.preventDefault(); submitEditPackage('${packageId}')">
          <div class="grid grid-cols-2 gap-4">
            <div class="col-span-2">
              <label class="block text-sm font-medium text-ink-700 mb-1.5">包体名称 <span class="text-danger-600">*</span></label>
              <input id="edit-pkg-name" required maxlength="60" value="${displayName}" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm outline-none focus:border-brand-500" />
              <p class="text-xs text-ink-500 mt-1">运营系统默认使用该名称区分包体。</p>
            </div>
            <div>
              <div class="flex items-center justify-between gap-2 mb-1.5">
                <label class="block text-sm font-medium text-ink-700">国家/区域 (Region) <span class="text-xs text-ink-400 font-normal">（可选）</span></label>
                <button type="button" onclick="document.getElementById('edit-pkg-mask')?.remove(); navigate('/admin/platform/regions-countries?return=' + encodeURIComponent(getCurrentPathWithQuery()))" class="text-xs text-brand-700 hover:underline">统一配置</button>
              </div>
              <select id="edit-pkg-region" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm bg-white">
                ${packageRegionSelectOptions(pkg.region)}
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-ink-700 mb-1.5">平台 (Platform) <span class="text-danger-600">*</span></label>
              <select id="edit-pkg-platform" required onchange="updateEditPackageAppIdLabel(this.value);" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm bg-white">
                ${EASYSHARE_PACKAGE_PLATFORMS.map(p => `<option value="${p.code}" ${normalizedPlatform === p.code ? "selected" : ""}>${p.zhName} / ${p.name}</option>`).join("")}
              </select>
            </div>
            <div class="col-span-2">
              <label class="block text-sm font-medium text-ink-700 mb-1.5">Bundle ID / Package Name <span class="text-danger-600">*</span></label>
              <input id="edit-pkg-bundle" required value="${pkg.bundle_id}" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm font-mono outline-none focus:border-brand-500" />
              <p class="text-xs text-ink-500 mt-1">应用包名或 Bundle 标识，用于 SDK 初始化、平台匹配和包体识别。</p>
            </div>
            <div class="col-span-2">
              <label id="edit-pkg-app-id-label" class="block text-sm font-medium text-ink-700 mb-1.5">${appIdLabel} <span class="text-danger-600">*</span></label>
              <input id="edit-pkg-app-id" required value="${pkg.app_id || creds.app_id}" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm font-mono outline-none focus:border-brand-500" />
              <p id="edit-pkg-app-id-help" class="text-xs text-ink-500 mt-1">iOS 体系填写 Apple App ID；Google Play 体系填写 Google Play App ID；其它三方平台填写平台app Id。</p>
            </div>
            <div class="col-span-2">
              <label class="block text-sm font-medium text-ink-700 mb-1.5">应用商店链接 <span class="text-danger-600">*</span></label>
              <input id="edit-pkg-store" type="url" required value="${pkg.store_url || ""}" class="w-full px-3 py-2.5 border border-ink-200 rounded-lg text-sm font-mono outline-none focus:border-brand-500" />
              <p class="text-xs text-ink-500 mt-1">用户点击分享链接后的最终跳转地址。</p>
            </div>
          </div>
          <div class="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2">
            <span>⚠️</span>
            <div>同一游戏下，<strong>Region + 平台</strong>组合需要唯一。包体名称由业务负责人维护，不会随 Region × Platform 自动改写。</div>
          </div>
          <div class="mt-3 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2 text-xs text-brand-900">说明：包体共享所属游戏的 GameID 与 Game Secret；编辑包体不会改变游戏级凭证。</div>
          <div class="mt-6 pt-5 border-t border-ink-100 flex justify-end gap-2">
            <button type="button" onclick="document.getElementById('edit-pkg-mask').remove()" class="px-4 py-2 border border-ink-200 rounded-lg text-sm hover:bg-ink-50">取消</button>
            <button type="submit" class="px-4 py-2 bg-brand-900 text-white rounded-lg text-sm hover:bg-brand-800">保存修改</button>
          </div>
        </form>
      </div>
    </div>`;
  document.body.insertAdjacentHTML("beforeend", html);
};

window.updateEditPackageAppIdLabel = function (platformText) {
  const label = document.getElementById("edit-pkg-app-id-label");
  const input = document.getElementById("edit-pkg-app-id");
  const help = document.getElementById("edit-pkg-app-id-help");
  if (!label || !input || !help) return;
  const meta = packagePlatformMeta(platformText);
  label.innerHTML = `${meta.appIdLabel || "平台 App ID"} <span class="text-danger-600">*</span>`;
  input.placeholder = meta.code === "APP_STORE" ? "如：1234567890" : "请填写该平台分配的应用 ID / 包名";
  help.textContent = `当前平台：${meta.zhName} / ${meta.name}（${meta.code}）。`;
};

window.updateEditPackageNamePreview = function () {
  // 包体名称由业务负责人维护，不再根据 Region / Platform 自动生成。
};

window.submitEditPackage = async function (packageId) {
  const pkg = MOCK.PACKAGES.find(p => p.id === packageId);
  if (!pkg) return;
  const old = { ...pkg };
  const packageName = document.getElementById("edit-pkg-name")?.value.trim();
  const regionRaw = document.getElementById("edit-pkg-region")?.value || "";
  const platformRaw = document.getElementById("edit-pkg-platform")?.value || "";
  const bundleId = document.getElementById("edit-pkg-bundle")?.value.trim();
  const appId = document.getElementById("edit-pkg-app-id")?.value.trim();
  const storeUrl = document.getElementById("edit-pkg-store")?.value.trim();

  if (!packageName || !platformRaw || !bundleId || !appId || !storeUrl) {
    UI.toast("请补全必填字段", "error");
    return;
  }

  const { region, regionName } = parsePackageRegion(regionRaw);
  const platform = packagePlatformCode(platformRaw);
  try {
    await packagesApi.update(packageId, {
      name: packageName,
      region,
      region_name: regionName,
      platform,
      bundle_id: bundleId,
      app_id: appId,
      store_url: storeUrl,
    });
    const game = MOCK.GAMES.find(g => g.id === pkg.game_id);
    await addPackageAuditLog({
      action: "编辑包体",
      target: `${game?.name || "未知游戏"} / ${packageDisplayName(old, game)}`,
      detail: `包体名称: ${old.name} → ${packageName}; Region: ${old.region} → ${region}; Platform: ${old.platform} → ${platform}; Bundle ID: ${old.bundle_id} → ${bundleId}; App ID: ${old.app_id || "—"} → ${appId}; Store URL: ${old.store_url || "—"} → ${storeUrl}`,
    });
    await invalidateAndRefresh();
    document.getElementById("edit-pkg-mask")?.remove();
    UI.toast("包体信息已更新，并已记录操作流水", "success");
    renderRoute();
  } catch (err) {
    UI.toast(err?.message || "保存失败", "error");
  }
};

// ---------- 删除包体（强预警 + 操作流水） ----------
window.openDeletePackageModal = function (packageId) {
  const pkg = MOCK.PACKAGES.find(p => p.id === packageId);
  if (!pkg) return;
  const game = MOCK.GAMES.find(g => g.id === pkg.game_id);
  const displayName = packageDisplayName(pkg, game);
  const html = `
    <div class="fixed inset-0 z-50 flex items-center justify-center" id="delete-pkg-mask">
      <div class="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]"></div>
      <div class="relative bg-white rounded-xl shadow-pop w-[560px] p-6 border-2 border-danger-200 animate-fade-up">
        <div class="flex items-start gap-3 mb-4">
          <div class="w-11 h-11 rounded-xl bg-danger-600 text-white flex items-center justify-center text-lg flex-shrink-0">⚠️</div>
          <div>
            <h3 class="text-lg font-semibold text-danger-700">删除包体</h3>
            <p class="text-xs text-danger-600 mt-1">这是高风险操作，删除后不可恢复。</p>
          </div>
        </div>
        <div class="bg-danger-50 border border-danger-200 rounded-lg p-3 text-sm text-danger-800 leading-relaxed">
          确定要删除 <strong>${displayName}</strong> 吗？
          <ul class="text-xs mt-2 space-y-1 list-disc ml-4">
            <li>该包体的分享配置、渠道覆盖配置将被删除</li>
            <li>该包体的 SDK 接入将失效</li>
            <li>历史数据将保留用于审计，但不可再写入新事件</li>
            <li>本操作会写入操作流水</li>
          </ul>
        </div>
        <label class="block text-sm text-ink-700 mt-4 mb-2">请输入包体名称 <code class="font-mono px-1.5 py-0.5 bg-ink-100 rounded text-danger-700">${displayName}</code> 以确认：</label>
        <input id="delete-pkg-input" type="text" placeholder="${displayName}" class="w-full px-3 py-2.5 border-2 border-ink-200 rounded-lg outline-none focus:border-danger-500 focus:ring-2 focus:ring-danger-100 text-sm font-mono" oninput="
          const ok = this.value === '${displayName}';
          document.getElementById('delete-pkg-btn').disabled = !ok;
          document.getElementById('delete-pkg-btn').className = ok ? 'px-4 py-2 rounded-lg bg-danger-600 text-white text-sm font-semibold' : 'px-4 py-2 rounded-lg bg-ink-100 text-ink-300 text-sm font-semibold cursor-not-allowed';
        " />
        <div class="mt-6 pt-5 border-t border-ink-100 flex justify-end gap-2">
          <button onclick="document.getElementById('delete-pkg-mask').remove()" class="px-4 py-2 border border-ink-200 rounded-lg text-sm hover:bg-ink-50">取消</button>
          <button id="delete-pkg-btn" disabled onclick="confirmDeletePackage('${packageId}')" class="px-4 py-2 rounded-lg bg-ink-100 text-ink-300 text-sm font-semibold cursor-not-allowed">确认删除</button>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML("beforeend", html);
  setTimeout(() => document.getElementById("delete-pkg-input")?.focus(), 100);
};

window.confirmDeletePackage = async function (packageId) {
  const pkg = MOCK.PACKAGES.find(p => p.id === packageId);
  if (!pkg) return;
  const game = MOCK.GAMES.find(g => g.id === pkg.game_id);
  try {
    await packagesApi.remove(packageId);
    await adminApi.createAuditLog({
      action: "删除包体",
      target: `${game?.name || "未知游戏"} / ${pkg.name}`,
      detail: `删除包体 ${pkg.region}-${pkg.platform}; Bundle ID: ${pkg.bundle_id}; 操作不可恢复`,
    });
    await invalidateAndRefresh();
    document.getElementById("delete-pkg-mask")?.remove();
    UI.toast("包体已删除，并已记录操作流水", "error");
    renderRoute();
  } catch (err) {
    UI.toast(err?.message || "删除失败", "error");
  }
};

// __window_exports__
// Expose helpers for cross-page / inline onclick compatibility
;(window as any).addPackageAuditLog = addPackageAuditLog;
;(window as any).bindGameCreateForm = bindGameCreateForm;
;(window as any).credField = credField;
;(window as any).formField = formField;
;(window as any).gameDetailInfoPill = gameDetailInfoPill;
;(window as any).gameDetailMetric = gameDetailMetric;
;(window as any).gameStatusBadge = gameStatusBadge;
;(window as any).generateGameId = generateGameId;
;(window as any).generateGameSecret = generateGameSecret;
;(window as any).generateRippleId = generateRippleId;
;(window as any).getShareCountryOptions = getShareCountryOptions;
;(window as any).getShareRegionCountryGroups = getShareRegionCountryGroups;
;(window as any).getShareRegionOptions = getShareRegionOptions;
;(window as any).infoCell = infoCell;
;(window as any).overviewCard = overviewCard;
;(window as any).overviewQuickActions = overviewQuickActions;
;(window as any).packageAppIdLabel = packageAppIdLabel;
;(window as any).packageDisplayName = packageDisplayName;
;(window as any).packageFilterBtn = packageFilterBtn;
;(window as any).packagePlatformCode = packagePlatformCode;
;(window as any).packagePlatformLabel = packagePlatformLabel;
;(window as any).packagePlatformMeta = packagePlatformMeta;
;(window as any).packageRegionSelectOptions = packageRegionSelectOptions;
;(window as any).packageRegionText = packageRegionText;
;(window as any).parsePackageRegion = parsePackageRegion;
;(window as any).pkgCreds = pkgCreds;
;(window as any).pkgStatusBadge = pkgStatusBadge;
;(window as any).regionFlag = regionFlag;
;(window as any).regionNameFromCode = regionNameFromCode;
;(window as any).regionOptionLabel = regionOptionLabel;
;(window as any).renderGameAccordion = renderGameAccordion;
;(window as any).renderGameTab = renderGameTab;
;(window as any).renderPackageRow = renderPackageRow;
;(window as any).renderPackagesPanel = renderPackagesPanel;
;(window as any).setFormDisabled = setFormDisabled;
;(window as any).setSubmitLoading = setSubmitLoading;
;(window as any).validateGameCreate = validateGameCreate;
